import { Box, Collapse, Divider, Typography, useTheme } from "@mui/material"
import React, { useState } from "react"

import TutorialsSection from '../components/admin/tutorials-and-manuals/TutorialsSection'
import VideoPlayer from "../components/common/video-player/VideoPlayer"
import CustomTabs from "../components/common/CustomTabs"
import { Icons } from "../utils/utilities"

// FACULTY / HOD / PRINCIPAL FAQS

const FacultyFaqs = [
    {
        section: "Online classes",
        faq: [
            {
                question: "How to join a class?",
                answer:
                    "Allow permissions such as camera and microphone in the browser. Go to home/dashboard page and under online classes section, join the scheduled class by clicking on join",
            },
            {
                question: "How to take attendance?",
                answer:
                    "Once joined the sessions attendence will be taken automatically in certain interval of time",
            },
            {
                question: "How to schedule online classes?",
                answer: "Add class to your time table using ERP and mark it as online",
            },
        ],
    },
    {
        section: "Exam",
        faq: [
            {
                question: "How to create an exam?",
                answer: (
                    <div className="faq-steps">
                        <div className="faq-step-box">
                            <span className="faq-step">1</span>
                            <p>
                                Click in the <em>+</em> at the bottom right corner of the screen
                                in exams page.
                            </p>
                        </div>

                        <div className="faq-step-box">
                            <span className="faq-step">2</span>
                            <p>
                                Fill all the required fields such as exam name, description,
                                topics, exam start and end time and batches.
                            </p>
                        </div>

                        <div className="faq-step-box">
                            <span className="faq-step">3</span>
                            <p>
                                Click on <em>create</em> button at the bottom.
                            </p>
                        </div>
                    </div>
                ),
            },
            {
                question: "How to add questions to an exam?",
                answer: (
                    <div>
                        <div className="faq-steps">
                            <h3>From a file</h3>

                            <div className="faq-step-box">
                                <span className="faq-step">1</span>
                                <p>
                                    Click on the <em>Import Question</em> button in exam page.
                                </p>
                            </div>

                            <div className="faq-step-box">
                                <span className="faq-step">2</span>
                                <p>
                                    Choose a text file in{" "}
                                    <a
                                        target="_blank"
                                        rel="noreferrer"
                                        href="https://docs.moodle.org/401/en/Aiken_format"
                                    >
                                        aiken
                                    </a>{" "}
                                    format to import questions.
                                </p>
                            </div>
                        </div>

                        <div className="faq-steps">
                            <h3>From the UI</h3>

                            <div className="faq-step-box">
                                <span className="faq-step">1</span>
                                <p>
                                    Click on the <em>Add Question</em> button in exam page.
                                </p>
                            </div>

                            <div className="faq-step-box">
                                <span className="faq-step">2</span>
                                <p>Fill all the required fields.</p>
                            </div>

                            <div className="faq-step-box">
                                <span className="faq-step">3</span>
                                <p>
                                    Click on the <em>Add</em> button to add new question.
                                </p>
                            </div>
                        </div>
                    </div>
                ),
            },
            {
                question: "Is the question restricted to english language only?",
                answer:
                    "No. question can be framed in any language in text file or through UI.",
            },
        ],
    },
    {
        section: "Study Material",
        faq: [
            {
                question: "What are the supported file formats?",
                answer: ".pdf, .docx, .ppsx, .pptx, .odp",
            },
            {
                question: "Can files be added after creation of study material?",
                answer: "Yes",
            },
            {
                question: "Which students have access to my notes?",
                answer:
                    "Any student with Alive access will be able to access any study material on Alive",
            },
        ],
    },
    {
        section: "Academic Progress",
        faq: [
            {
                question: "How to complete proctee's checklist?",
                mediaAttachments: [
                    {
                        type: "video",
                        src: "/videos/academics-audit-video.mp4",
                    },
                ],
                answer: (
                    <div>
                        <p>
                            Proctor should collect details from the student and fill it. The
                            steps to perform this task can be viewed in the video below.
                        </p>
                    </div>
                ),
            },
        ],
    },
]

// STUDENT FAQS

const StudentFaqs = [
    {
        section: "Online Classes",
        faq: [
            {
                question: "How to join a class?",
                answer:
                    "Allow permissions such as camera and microphone in the browser. Go to home/dashboard page and under online classes section, join the scheduled class by clicking on join",
            },
        ],
    },
    {
        section: "Exam",
        faq: [
            {
                question: "Can exams be taken on mobile devices?",
                answer: "Yes. But a device with larger sceen is recommended.",
            },
            {
                question: "Is fullscreen mandatory while taking the exam?",
                answer: "Yes. If fullscreen mode is exited the exam will be ended.",
            },
            {
                question: "What happens if window focus is changes during the exam?",
                answer: "The exam will be ended.",
            },
        ],
    },
    {
        section: "Study Material",
        faq: [
            {
                question: "Can study materials of other branch are accessible",
                answer: "Yes.",
            },
            {
                question: "Is it possible to dowload entire study material?",
                answer: "Yes. In a compressed zip file",
            },
        ],
    },
]

// HELP CENTER TABS

const HELP_CENTER_TABS = [
    { value: "faq", label: "FAQs" },
    { value: "tutorials", label: "Tutorials" },
    { value: "manuals", label: "Manuals" },
]

// FAQ SECTION

const FAQSection = ( { auth } ) => {
    const [collapseState, setCollapseState] = useState( {} )
    const { palette, layout } = useTheme()

    const faqSections =
        auth?.toLowerCase() === "student" ? StudentFaqs : FacultyFaqs

    const toggleFaq = ( collapseKey ) => {
        setCollapseState( ( prev ) => ( {
            ...prev,
            [collapseKey]: !prev[collapseKey],
        } ) )
    }

    return (
        <Box display="flex" flexDirection="column" gap="20px" marginTop="20px">
            {faqSections.map( ( section ) => (
                <Box key={section.section}>
                    <Typography variant="subtitle2" gutterBottom>
                        {section.section}
                    </Typography>

                    <Box display="flex" flexDirection="column" gap="10px">
                        {section.faq.map( ( item, index ) => {
                            const collapseKey = `${section.section}-${index}`
                            const isOpen = Boolean( collapseState[collapseKey] )

                            return (
                                <Box
                                    key={collapseKey}
                                    bgcolor={palette.contentBg}
                                    borderRadius={layout.borderRadius}
                                    overflow="clip"
                                >
                                    <Box
                                        onClick={() => toggleFaq( collapseKey )}
                                        sx={{ cursor: "pointer" }}
                                    >
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap="10px"
                                            padding="5px"
                                            color={palette.common.font}
                                            sx={{
                                                background: isOpen
                                                    ? palette.greyedOut + "dd"
                                                    : palette.greyedOut + "66",
                                            }}
                                        >
                                            {isOpen
                                                ? Icons.default.RemoveIcon
                                                : Icons.default.AddIcon}

                                            <Typography variant="subtitle1" fontWeight="600">
                                                {item.question}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Collapse
                                        in={isOpen}
                                        sx={{
                                            background: palette.greyedOut + "dd",
                                        }}
                                    >
                                        <Divider
                                            sx={{
                                                background: palette.common.bg,
                                            }}
                                        />

                                        <Box
                                            color={palette.common.font}
                                            fontSize="14px"
                                            padding="10px"
                                        >
                                            {item.answer}

                                            {item.mediaAttachments && (
                                                <Box
                                                    width="100%"
                                                    maxWidth="500px"
                                                    display="flex"
                                                    flexDirection="column"
                                                    marginTop="10px"
                                                >
                                                    {item.mediaAttachments.map( ( media, mediaIndex ) => {
                                                        if ( media.type !== "video" ) {
                                                            return null
                                                        }

                                                        return (
                                                            <VideoPlayer
                                                                key={mediaIndex}
                                                                title={item.question}
                                                                src={media.src}
                                                                controls={false}
                                                            />
                                                        )
                                                    } )}
                                                </Box>
                                            )}
                                        </Box>
                                    </Collapse>
                                </Box>
                            )
                        } )}
                    </Box>
                </Box>
            ) )}
        </Box>
    )
}

// HELP CENTER

const FAQs = ( { auth } ) => {
    const normalizedAuth = auth?.toLowerCase() || ""

    // ADMIN / SUBADMIN
    // Admin sees Tutorial Management directly.
    // No FAQ / Tutorial / Manuals tabs.

    const isAdminHelpCenter = ["admin", "subadmin"].includes( normalizedAuth )

    // Normal users initially open FAQs.

    const [activeTab, setActiveTab] = useState(
        isAdminHelpCenter ? "tutorials" : "faq",
    )

    return (
        <Box flexGrow={1} minWidth="0" overflow="auto" padding="20px">
            <Box>
                <Typography
                    variant="h1"
                    fontWeight="600"
                    component="h1"
                    fontSize="16px"
                >
                    {isAdminHelpCenter ? "Alive Feature Tutorials and Manuals Management" : "Quick Guides: Alive Feature Demos and Manuals"}
                </Typography>

                <Typography variant="subtitle2" fontSize="12px" color="textSecondary">
                    {isAdminHelpCenter
                        ? "Add, search, update and manage Alive tutorial videos."
                        : "Find answers and learn how to use Alive."}
                </Typography>
            </Box>

            {!isAdminHelpCenter && (
                <Box marginTop="20px">
                    <CustomTabs
                        value={activeTab}
                        handleTabChange={setActiveTab}
                        tabs={HELP_CENTER_TABS}
                        variant="aligned-left"
                    />
                </Box>
            )}

            {isAdminHelpCenter && <TutorialsSection auth={auth} showTabs={true} />}

            {!isAdminHelpCenter && activeTab === "faq" && <FAQSection auth={auth} />}

            {!isAdminHelpCenter && activeTab === "tutorials" && (
                <TutorialsSection
                    auth={auth}
                    contentType="tutorials"
                    showTabs={false}
                />
            )}

            {!isAdminHelpCenter && activeTab === "manuals" && (
                <TutorialsSection auth={auth} contentType="manuals" showTabs={false} />
            )}
        </Box>
    )
}

export default FAQs
